import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  Add as AddIcon,
  DeleteOutline as DeleteIcon,
  Send as SendIcon,
  ArrowDropDown as ArrowDropDownIcon,
  AttachFile as AttachFileIcon,
  AutoAwesome as AutoAwesomeIcon,
  Visibility as PreviewIcon,
  InsertDriveFile as FileIcon,
} from "@mui/icons-material";
import ResizablePanel from "../../components/common/ResizablePanel";
import { AddressbookPanelShell } from "../../components/component-contact";
import { contactCardSx } from "../../components/component-contact/ContactCard";
import {
  apiGetBlocks,
  apiCreateBlock,
  apiUpdateBlock,
  apiDeleteBlock,
} from "../../graphQL/blocks";
import { apiGetUser } from "../../graphQL/user";
import { EditOutlined as EditIcon } from "@mui/icons-material";
import { myToast } from "../../libs";
import { v4 as uuid_v4 } from "../../libs/uuid";
import config from "../../config";
import { useUploadContext } from "../../Provider/UploadContext";
import { apiCreateFile, apiUpdateFile, apiDeleteFile } from "../../graphQL/file";
import { apiGetAllAgents } from "../../graphQL/agent";
import PreviewFile from "../../components/files/PreviewFile";
import { UI_TOGGLES } from "../../config/uiToggles";

// Everything lives in the shared `blocks` collection.
//  - A feedback item is a Block (schemaType "feedback").
//  - Each conversation reply is a child Block (schemaType "feedbackResponse",
//    parentId = the item's id).
const SCHEMA_ITEM = "feedback";
const SCHEMA_RESPONSE = "feedbackResponse";

const TYPES = [
  { value: "featureRequest", label: "Feature Request", accent: "#3B82F6" },
  { value: "bugReport", label: "Bug Report", accent: "#EF4444" },
  { value: "dataIngest", label: "Data Ingest", accent: "#3F51B5" },
];

// "deleted" is a soft-delete status: hidden from the list, not offered in the
// status dropdown.
const STATUSES = [
  { value: "open", label: "Open", bg: "#DBEAFE", fg: "#1E40AF" },
  { value: "in-progress", label: "In Progress", bg: "#FEF3C7", fg: "#92400E" },
  { value: "resolved", label: "Resolved", bg: "#DCFCE7", fg: "#166534" },
  { value: "closed", label: "Closed", bg: "#F1F5F9", fg: "#475569" },
  { value: "wont-do", label: "Won't Do", bg: "#FEE2E2", fg: "#991B1B" },
];

// Related button/feature options -- sourced from the app-wide UI toggle
// registry so a ticket can point at the concrete control it concerns.
// Labels are prefixed by their Settings-tab area.
const BUTTON_OPTIONS = UI_TOGGLES.map((t) => ({
  key: t.key,
  label: `${t.area} \u2014 ${t.label}`,
}));
const buttonLabelForKey = (key) =>
  (BUTTON_OPTIONS.find((o) => o.key === key) || {}).label || key;

// Resolve authorId -> a friendly display name, cached across renders/items.
const authorCache = new Map();
const displayFromUser = (u, id) => {
  const n = String(u?.name || u?.username || "").trim();
  if (n.includes("@")) return n.split("@")[0];
  return n || (id ? String(id).slice(0, 8) : "Unknown");
};
const useAuthorNames = (ids) => {
  const [, force] = useState(0);
  const key = ids.filter(Boolean).join(",");
  useEffect(() => {
    const missing = [...new Set(ids)].filter((id) => id && !authorCache.has(id));
    if (!missing.length) return undefined;
    let alive = true;
    Promise.all(
      missing.map(async (id) => {
        try { authorCache.set(id, displayFromUser(await apiGetUser(id), id)); }
        catch { authorCache.set(id, String(id).slice(0, 8)); }
      })
    ).then(() => { if (alive) force((n) => n + 1); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return (id) => authorCache.get(id) || (id ? String(id).slice(0, 8) : "Unknown");
};

const typeMeta = (v) => TYPES.find((t) => t.value === v) || { label: v || "Item", accent: "#94A3B8" };
const statusMeta = (v) => STATUSES.find((s) => s.value === v) || { label: v || "open", bg: "#E2E8F0", fg: "#1F2937" };

const fmtTime = (ts) => {
  const n = typeof ts === "number" ? ts : Number(ts) || Date.parse(ts);
  if (!n || Number.isNaN(n)) return "";
  const d = new Date(n);
  const diff = Date.now() - d.getTime();
  if (diff < 60000) return "just now";
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

/* ─────────────────────────── List card ─────────────────────────── */

const FeedbackCard = ({ item, selected, onSelect, onDelete, onStatusChange, authorName }) => {
  const tm = typeMeta(item.type);
  const sm = statusMeta(item.category);
  const isDraft = item._isNew;
  return (
    <Box sx={contactCardSx.card(selected, undefined, tm.accent)} onClick={() => onSelect(item)}>
      <Box sx={contactCardSx.cardHeader}>
        <Typography sx={contactCardSx.cardName}>
          {item.data?.ticketNumber ? (
            <Box component="span" sx={{ color: "#64748b", fontWeight: 700, mr: 0.5 }}>#{item.data.ticketNumber}</Box>
          ) : null}
          {item.name || (isDraft ? "New request" : "Untitled")}
        </Typography>
        {!isDraft && (onStatusChange ? (
          <Box component="span" onClick={(e) => e.stopPropagation()} sx={{ display: "inline-flex", flexShrink: 0 }}>
            <StatusPillSelect value={item.category} onChange={(next) => onStatusChange(item, next)} />
          </Box>
        ) : (
          <Box component="span" sx={contactCardSx.statusChip(sm.bg, sm.fg)}>{sm.label}</Box>
        ))}
      </Box>
      <Typography sx={{ ...contactCardSx.cardTitle, color: tm.accent, fontWeight: 600 }}>
        {tm.label}
      </Typography>
      {item.data?.buttonKey ? (
        <Box component="span" sx={{ ...contactCardSx.statusChip("#F1F5F9", "#64748B"), mt: 0.5, maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {item.data.buttonLabel || item.data.buttonKey}
        </Box>
      ) : null}
      {item.desc ? (
        <Typography
          sx={{
            fontSize: 12, color: "#6B7280", mt: 0.4,
            overflow: "hidden", textOverflow: "ellipsis",
            display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
          }}
        >
          {item.desc}
        </Typography>
      ) : null}
      <Box sx={contactCardSx.cardFooter}>
        <Typography sx={{ fontSize: 11, color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {isDraft ? "Draft" : (
            <>
              <Box component="span" sx={{ fontWeight: 700, color: "#334155" }}>{authorName || "Unknown"}</Box>
              {" · "}{fmtTime(item.updatedAt || item.timestamp)}
            </>
          )}
        </Typography>
        <IconButton
          size="small"
          sx={contactCardSx.iconBtn}
          onClick={(e) => { e.stopPropagation(); onDelete(item); }}
          aria-label="Delete request"
        >
          <DeleteIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
    </Box>
  );
};

/* ─────────────────── New request (inline, JIRA style) ─────────────────── */

const NewRequestForm = ({ onCreate, onCancel, saving }) => {
  const [type, setType] = useState("featureRequest");
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [buttonKey, setButtonKey] = useState("");
  return (
    <Box>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.secondary", mb: 1 }}>
        New request
      </Typography>
      {/* First row: type + the summary / title on one line, like JIRA */}
      <Stack direction="row" spacing={1.5} alignItems="center" useFlexGap sx={{ mb: 1.5, flexWrap: "wrap" }}>
        <TextField select size="small" label="Type" value={type} onChange={(e) => setType(e.target.value)} sx={{ minWidth: 170, flexShrink: 0 }}>
          {TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Related button" value={buttonKey} onChange={(e) => setButtonKey(e.target.value)} sx={{ minWidth: 200, flexShrink: 0 }}>
          <MenuItem value="" sx={{ fontSize: 12 }}>None / not specific</MenuItem>
          {BUTTON_OPTIONS.map((o) => <MenuItem key={o.key} value={o.key} sx={{ fontSize: 12 }}>{o.label}</MenuItem>)}
        </TextField>
        <TextField
          placeholder="What do you need? (title)"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); if (title.trim()) onCreate({ type, title, desc, buttonKey }); } }}
          fullWidth
          autoFocus
          variant="standard"
          InputProps={{ disableUnderline: false, sx: { fontSize: 18, fontWeight: 700 } }}
        />
        <Chip size="small" label="Open" sx={{ backgroundColor: "#DBEAFE", color: "#1E40AF", fontWeight: 600, flexShrink: 0 }} />
      </Stack>
      <TextField
        placeholder="Add a description..."
        value={desc}
        onChange={(e) => setDesc(e.target.value)}
        fullWidth
        multiline
        minRows={4}
        sx={{ mb: 2 }}
      />
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button variant="contained" disabled={saving || !title.trim()} onClick={() => onCreate({ type, title, desc, buttonKey })}>
          {saving ? "Creating..." : "Create"}
        </Button>
      </Stack>
    </Box>
  );
};

/* ─────────────────────── Conversation (child blocks) ─────────────────────── */

const Conversation = ({ item, authorId, domain }) => {
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const nameOf = useAuthorNames(responses.map((r) => r.authorId));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiGetBlocks({
        schemaType: SCHEMA_RESPONSE,
        parentId: item.id,
        ordering: { field: "timestamp", order: "Asc" },
        pagination: { page: 1, limit: 200 },
      });
      setResponses(Array.isArray(res?.blocks) ? res.blocks : []);
    } catch (err) {
      console.error("Failed to load conversation:", err);
    } finally {
      setLoading(false);
    }
  }, [item.id]);

  useEffect(() => { load(); }, [load]);

  const send = async () => {
    const body = text.trim();
    if (!body || !authorId) return;
    setSending(true);
    try {
      await apiCreateBlock({
        schemaType: SCHEMA_RESPONSE,
        type: "response",
        parentId: item.id,
        desc: body,
        ...(domain ? { domain } : {}),
        ...(authorId ? { authorId } : {}),
      });
      setText("");
      await load();
    } catch (err) {
      console.error("Failed to post response:", err);
    } finally {
      setSending(false);
    }
  };

  const saveEdit = async (r) => {
    const body = editText.trim();
    if (!body) return;
    try {
      await apiUpdateBlock({ id: r.id, desc: body, updateNote: "edited response" });
      setEditingId(null);
      setEditText("");
      await load();
    } catch (err) {
      console.error("Failed to edit response:", err);
    }
  };

  const remove = async (r) => {
    try {
      await apiDeleteBlock(r.id);
      await load();
    } catch (err) {
      console.error("Failed to delete response:", err);
    }
  };

  return (
    <Box>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}><CircularProgress size={20} /></Box>
      ) : responses.length > 0 ? (
        <Stack spacing={1.25} sx={{ mb: 1.5 }}>
          {responses.map((r) => (
            <Box key={r.id} sx={{ p: 1.25, borderRadius: 1.5, border: "1px solid", borderColor: "divider", backgroundColor: "#F8FAFC" }}>
              {/* action line: author + timestamp | edit + delete icons */}
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                  <Box component="span" sx={{ fontWeight: 700, color: "#334155" }}>{nameOf(r.authorId)}</Box>
                  {" · "}{fmtTime(r.timestamp)}{r.updatedAt !== r.timestamp ? " (edited)" : ""}
                </Typography>
                <Stack direction="row" spacing={0.25}>
                  <IconButton size="small" aria-label="Edit response" onClick={() => { setEditingId(r.id); setEditText(r.desc || ""); }} sx={{ width: 24, height: 24, color: "#64748B", "&:hover": { color: "#3B82F6", backgroundColor: "#EFF6FF" } }}>
                    <EditIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                  <IconButton size="small" aria-label="Delete response" onClick={() => remove(r)} sx={{ width: 24, height: 24, color: "#94A3B8", "&:hover": { color: "#EF4444", backgroundColor: "#FEE2E2" } }}>
                    <DeleteIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Stack>
              </Box>
              {editingId === r.id ? (
                <Stack spacing={1}>
                  <TextField value={editText} onChange={(e) => setEditText(e.target.value)} multiline minRows={2} fullWidth size="small" />
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button size="small" onClick={() => { setEditingId(null); setEditText(""); }}>Cancel</Button>
                    <Button size="small" variant="contained" onClick={() => saveEdit(r)}>Save</Button>
                  </Stack>
                </Stack>
              ) : (
                <Typography sx={{ fontSize: 13, whiteSpace: "pre-wrap" }}>{r.desc || ""}</Typography>
              )}
            </Box>
          ))}
        </Stack>
      ) : null}

      <Stack direction="row" spacing={1} alignItems="flex-end">
        <TextField
          placeholder="Add a response..."
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(); }}
          fullWidth multiline minRows={1} maxRows={5} size="small"
        />
        <IconButton color="primary" onClick={send} disabled={sending || !text.trim()}>
          <SendIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Box>
  );
};

/* ───────────────── Data Ingest: file attach + AI summary ───────────────── */

// Reuses the existing file pipeline end-to-end:
//  - upload:  UploadContext.uploadSingle -> S3 (same helper FilesPanel uses)
//  - file doc: apiCreateFile / apiUpdateFile (graphQL/file)
//  - summary:  PreviewFile dialog (autoGenerateSummary) -> aiEngine FileSummary agent
// Attachment references live only on the feedback block's freeform data bag
// (data.ingestFiles[]) — no new top-level block fields.

// PreviewFile tab order for a normal (non-system) file is View=0, Summary=1.
const PREVIEW_SUMMARY_TAB = 1;

const inferExtension = (file) => {
  const fromName = String(file?.name || "").split(".").pop()?.trim().toLowerCase();
  if (fromName && fromName !== String(file?.name || "").toLowerCase()) return fromName;
  return String(file?.type || "").split("/").pop()?.trim().toLowerCase() || "";
};

// Map a stored data.ingestFiles[] entry to the shape PreviewFile expects.
const ingestEntryToPreviewFile = (f) => ({
  id: f.fileId,
  url: f.url,
  publicUrl: f.publicUrl || "",
  key: f.key,
  mimeType: f.mimeType,
  type: f.type || "document",
  fileCategory: f.fileCategory || "ingest",
  size: f.size,
  name: f.name,
  originalName: f.name,
  description: f.name,
  summary: f.summary || "",
  isPublic: false,
  authorId: f.authorId,
  data: f.data || {},
});

const DataIngestFiles = ({ item, authorId, domain, onSaveData }) => {
  const uploadCtx = useUploadContext();
  const PrivateInfo = useSelector((store) => store.auth.PrivateInfo);
  const effectiveAuthorId = PrivateInfo?.id || authorId || "";

  const [files, setFiles] = useState(() =>
    Array.isArray(item.data?.ingestFiles) ? item.data.ingestFiles : []
  );
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [agents, setAgents] = useState([]);
  const [preview, setPreview] = useState(null);
  const [autoGen, setAutoGen] = useState(false);
  const inputRef = useRef(null);

  // Reset local list only when switching to a different item.
  useEffect(() => {
    setFiles(Array.isArray(item.data?.ingestFiles) ? item.data.ingestFiles : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id]);

  // Agents power the PreviewFile summary pipeline (needs the FileSummary agent).
  useEffect(() => {
    let alive = true;
    apiGetAllAgents({})
      .then((r) => { if (alive) setAgents(r?.agents || []); })
      .catch(() => { if (alive) setAgents([]); });
    return () => { alive = false; };
  }, []);

  const persist = useCallback(
    async (next) => {
      setFiles(next);
      try {
        await onSaveData(item, { ingestFiles: next });
      } catch (err) {
        console.error("Failed to persist ingest files:", err);
      }
    },
    [item, onSaveData]
  );

  const doUpload = (file) => {
    if (!effectiveAuthorId) {
      myToast.error("Missing author context. Please sign in again.");
      return;
    }
    setUploading(true);
    setProgress(0);

    const fileId = uuid_v4();
    const domainKey = String(domain || "myrighthand").trim() || "myrighthand";
    const directory = `${domainKey}/${item.id}/files`;
    const fileName = fileId;
    const extension = inferExtension(file);
    const uploadKey = `${directory}/${fileName}`;
    const privateUrl = `${config.ENV.REACT_APP_HLS_BASE_URL}/${directory}/${fileName}`;

    uploadCtx.uploadSingle(
      directory,
      file,
      fileName,
      (uploadProgress) => setProgress(uploadProgress),
      async (err) => {
        if (err) {
          setUploading(false);
          setProgress(0);
          myToast.error(err?.message || "Upload failed");
          return;
        }
        try {
          const doc = await apiCreateFile({
            authorId: effectiveAuthorId,
            fileCategory: "ingest",
            type: "Data Ingest",
            fileType: extension,
            mimeType: file.type || "application/octet-stream",
            url: privateUrl,
            key: uploadKey,
            size: String(file.size),
            description: file.name,
            domain: domain || "",
            ref: { id: item.id, collection: "blocks" },
            status: "active",
            isPublic: false,
          });

          const entry = {
            fileId: doc.id,
            name: file.name,
            mimeType: file.type || "",
            size: file.size,
            url: privateUrl,
            key: uploadKey,
            fileCategory: "ingest",
            type: "Data Ingest",
            authorId: effectiveAuthorId,
            summary: "",
            uploadedAt: Date.now(),
          };
          await persist([...files, entry]);
          setUploading(false);
          setProgress(0);
          myToast.success("File attached — generating AI summary...");
          // Auto-open the summary pipeline so the AI reads the file immediately.
          setAutoGen(true);
          setPreview(ingestEntryToPreviewFile(entry));
        } catch (saveErr) {
          console.error("Failed to save ingest file:", saveErr);
          setUploading(false);
          setProgress(0);
          myToast.error(saveErr?.message || "Failed to attach file");
        }
      },
      { key: uploadKey, isPublic: false }
    );
  };

  const handlePick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) doUpload(file);
  };

  // PreviewFile persists the generated summary through this callback.
  const handleSaveSummary = async ({ summary, prompt, aiFileID, fileId }) => {
    const targetId = fileId || preview?.id;
    if (!targetId || summary === undefined) return;
    try {
      await apiUpdateFile({
        id: targetId,
        authorId: effectiveAuthorId,
        summary,
        data: { prompt },
        ...(aiFileID !== undefined ? { aiFileId: aiFileID } : {}),
      });
    } catch (err) {
      console.error("Failed to save summary on file doc:", err);
    }
    const next = files.map((f) =>
      f.fileId === targetId ? { ...f, summary } : f
    );
    await persist(next);
    setPreview((p) => (p && p.id === targetId ? { ...p, summary } : p));
  };

  const openView = (f) => {
    setAutoGen(false);
    setPreview(ingestEntryToPreviewFile(f));
  };

  const removeFile = async (f) => {
    try {
      await apiDeleteFile(f.fileId, f.authorId || effectiveAuthorId);
    } catch (err) {
      console.error("Failed to delete ingest file:", err);
    }
    await persist(files.filter((x) => x.fileId !== f.fileId));
  };

  const summarizingId = autoGen && preview ? preview.id : null;

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#283593" }}>
          Data files &amp; AI summary
        </Typography>
        <Button
          size="small"
          variant="contained"
          startIcon={<AttachFileIcon sx={{ fontSize: 16 }} />}
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          sx={{
            textTransform: "none",
            backgroundColor: "#3F51B5",
            "&:hover": { backgroundColor: "#3949AB" },
            boxShadow: "0 2px 6px rgba(63,81,181,0.25)",
          }}
        >
          {uploading ? "Uploading..." : "Attach file"}
        </Button>
        <input
          ref={inputRef}
          type="file"
          hidden
          onChange={handlePick}
        />
      </Box>

      {uploading ? (
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{ mb: 1, borderRadius: 1, "& .MuiLinearProgress-bar": { backgroundColor: "#536DFE" } }}
        />
      ) : null}

      {files.length === 0 ? (
        <Box sx={{ py: 2, px: 1.5, borderRadius: 1.5, border: "1px dashed #C5CAE9", backgroundColor: "#F4F5FB" }}>
          <Typography sx={{ fontSize: 12, color: "#5C6BC0" }}>
            Attach a file and the AI will read it and produce a summary so anyone can understand what it is.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1}>
          {files.map((f) => (
            <Box
              key={f.fileId}
              sx={{
                p: 1.25,
                borderRadius: 1.5,
                border: "1px solid #E8EAF6",
                backgroundColor: "#F4F5FB",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <FileIcon sx={{ fontSize: 18, color: "#3F51B5" }} />
                <Typography
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#1A1F36",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={f.name}
                >
                  {f.name}
                </Typography>
                <Tooltip title="View file & summary">
                  <IconButton
                    size="small"
                    onClick={() => openView(f)}
                    sx={{ width: 26, height: 26, color: "#3F51B5", "&:hover": { backgroundColor: "#E8EAF6" } }}
                  >
                    <PreviewIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title={f.summary ? "Regenerate summary" : "Generate AI summary"}>
                  <IconButton
                    size="small"
                    onClick={() => { setAutoGen(true); setPreview(ingestEntryToPreviewFile(f)); }}
                    sx={{ width: 26, height: 26, color: "#536DFE", "&:hover": { backgroundColor: "#E8EAF6" } }}
                  >
                    <AutoAwesomeIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Remove file">
                  <IconButton
                    size="small"
                    onClick={() => removeFile(f)}
                    sx={{ width: 26, height: 26, color: "#94A3B8", "&:hover": { color: "#EF4444", backgroundColor: "#FEE2E2" } }}
                  >
                    <DeleteIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>

              {summarizingId === f.fileId && !f.summary ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.75, pl: 0.25 }}>
                  <CircularProgress size={13} sx={{ color: "#536DFE" }} />
                  <Typography sx={{ fontSize: 12, color: "#5C6BC0", fontStyle: "italic" }}>
                    AI is reading the file...
                  </Typography>
                </Box>
              ) : f.summary ? (
                <Typography
                  sx={{
                    mt: 0.75,
                    fontSize: 12.5,
                    color: "#334155",
                    whiteSpace: "pre-wrap",
                    display: "-webkit-box",
                    WebkitLineClamp: 6,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {f.summary}
                </Typography>
              ) : (
                <Typography sx={{ mt: 0.75, fontSize: 12, color: "#8892B0", fontStyle: "italic" }}>
                  No summary yet — open or use the spark button to generate one.
                </Typography>
              )}
            </Box>
          ))}
        </Stack>
      )}

      {preview ? (
        <PreviewFile
          open={Boolean(preview)}
          file={preview}
          agents={agents}
          initialTab={PREVIEW_SUMMARY_TAB}
          autoGenerateSummary={autoGen}
          onClose={() => { setPreview(null); setAutoGen(false); }}
          onDownload={(f) => window.open(f.url, "_blank")}
          onSaveSummary={handleSaveSummary}
        />
      ) : null}
    </Box>
  );
};

/* ─────────────────────────── Detail body ─────────────────────────── */

// The status pill itself is the dropdown: click it to pick a new status.
const StatusPillSelect = ({ value, onChange }) => {
  const [anchor, setAnchor] = useState(null);
  const sm = statusMeta(value && value !== "deleted" ? value : "open");
  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{
          ...contactCardSx.statusChip(sm.bg, sm.fg),
          border: "none", cursor: "pointer", gap: 0.25, height: 22,
        }}
      >
        {sm.label}
        <ArrowDropDownIcon sx={{ fontSize: 15, ml: -0.25 }} />
      </Box>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {STATUSES.map((s) => (
          <MenuItem
            key={s.value}
            selected={s.value === value}
            onClick={() => { onChange(s.value); setAnchor(null); }}
            sx={{ fontSize: 13, gap: 1 }}
          >
            <Box component="span" sx={contactCardSx.statusChip(s.bg, s.fg)}>{s.label}</Box>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

const FeedbackDetailPanel = ({ item, authorId, domain, onStatusChange, onSave, onSaveData, onDelete }) => {
  const tm = typeMeta(item.type);
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(item.name || "");
  const [desc, setDesc] = useState(item.desc || "");
  const [savingEdit, setSavingEdit] = useState(false);
  const [buttonKey, setButtonKey] = useState(item.data?.buttonKey || "");
  const nameOf = useAuthorNames([item.authorId]);

  const beginEdit = () => { setTitle(item.name || ""); setDesc(item.desc || ""); setButtonKey(item.data?.buttonKey || ""); setEditing(true); };
  const commitEdit = async () => {
    setSavingEdit(true);
    try {
      const patch = { name: title.trim(), desc: desc.trim() };
      if ((item.data?.buttonKey || "") !== buttonKey) {
        const nextData = { ...(item.data || {}) };
        if (buttonKey) { nextData.buttonKey = buttonKey; nextData.buttonLabel = buttonLabelForKey(buttonKey); }
        else { delete nextData.buttonKey; delete nextData.buttonLabel; }
        patch.data = nextData;
      }
      await onSave(item, patch);
      setEditing(false);
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <AddressbookPanelShell
      titleContent={
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flex: 1, minWidth: 0 }}>
          <Chip size="small" label={tm.label} sx={{ backgroundColor: tm.accent, color: "#fff", fontWeight: 600, height: 22, flexShrink: 0 }} />
          <StatusPillSelect value={item.category} onChange={onStatusChange} />
          <Typography
            sx={{ fontSize: 13, fontWeight: 700, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}
          >
            {item.name || "Untitled"}
          </Typography>
        </Box>
      }
      headerActions={
        <>
          {!editing && (
            <IconButton size="small" aria-label="Edit request" onClick={beginEdit} sx={{ width: 24, height: 24, color: "#64748B", "&:hover": { color: "#3B82F6", backgroundColor: "#EFF6FF" } }}>
              <EditIcon sx={{ fontSize: 16 }} />
            </IconButton>
          )}
          <IconButton size="small" aria-label="Delete request" onClick={() => onDelete(item)} sx={{ width: 24, height: 24, color: "#94A3B8", "&:hover": { color: "#EF4444", backgroundColor: "#FEE2E2" } }}>
            <DeleteIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </>
      }
    >
      {editing ? (
        <Box>
          <TextField
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth variant="standard" autoFocus placeholder="Title"
            InputProps={{ sx: { fontSize: 18, fontWeight: 700 } }}
            sx={{ mb: 1.5 }}
          />
          <TextField
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            fullWidth multiline minRows={4} placeholder="Description"
            sx={{ mb: 1.5 }}
          />
          <TextField
            select size="small" label="Related button" value={buttonKey} onChange={(e) => setButtonKey(e.target.value)}
            fullWidth sx={{ mb: 1.5 }}
          >
            <MenuItem value="" sx={{ fontSize: 12 }}>None / not specific</MenuItem>
            {BUTTON_OPTIONS.map((o) => <MenuItem key={o.key} value={o.key} sx={{ fontSize: 12 }}>{o.label}</MenuItem>)}
          </TextField>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button size="small" onClick={() => setEditing(false)} disabled={savingEdit}>Cancel</Button>
            <Button size="small" variant="contained" onClick={commitEdit} disabled={savingEdit || !title.trim()}>
              {savingEdit ? "Saving..." : "Save"}
            </Button>
          </Stack>
        </Box>
      ) : (
        <>
          {item.desc ? (
            <Typography sx={{ fontSize: 14, whiteSpace: "pre-wrap" }}>{item.desc}</Typography>
          ) : (
            <Typography sx={{ fontSize: 13, color: "text.secondary", fontStyle: "italic" }}>No description.</Typography>
          )}
          {item.data?.buttonKey ? (
            <Box sx={{ mt: 1 }}>
              <Box component="span" sx={contactCardSx.statusChip("#F1F5F9", "#64748B")}>{item.data.buttonLabel || item.data.buttonKey}</Box>
            </Box>
          ) : null}
          <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 1 }}>
            Submitted by <Box component="span" sx={{ fontWeight: 700, color: "#334155" }}>{nameOf(item.authorId)}</Box> {fmtTime(item.timestamp)}
          </Typography>
        </>
      )}

      {item.type === "dataIngest" ? (
        <DataIngestFiles
          item={item}
          authorId={authorId}
          domain={domain}
          onSaveData={onSaveData}
        />
      ) : null}

      <Divider sx={{ my: 2 }} />

      <Conversation item={item} authorId={authorId} domain={domain} />
    </AddressbookPanelShell>
  );
};

/* ─────────────────────────── Page ─────────────────────────── */

const DRAFT_PREFIX = "draft-";

const PageFeedback = () => {
  const authorId = useSelector((state) => state.auth.userId);
  const user = useSelector((state) => state.auth.user);
  const userDomain = String(user?.domain || "").trim();
  const activeDomain = userDomain && userDomain !== "All" ? userDomain : "";
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [saving, setSaving] = useState(false);

  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });
  const notify = useCallback((message, severity = "success") => setToast({ open: true, message, severity }), []);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiGetBlocks({
        schemaType: SCHEMA_ITEM,
        ...(activeDomain ? { domain: activeDomain } : {}),
        ...(typeFilter ? { type: typeFilter } : {}),
        ordering: { field: "updatedAt", order: "Desc" },
        pagination: { page: 1, limit: 200 },
      });
      const list = Array.isArray(res?.blocks) ? res.blocks : [];
      setItems(list.filter((b) => b.category !== "deleted"));
    } catch (err) {
      console.error("Failed to load feedback:", err);
      notify("Could not load feedback", "error");
    } finally {
      setLoading(false);
    }
  }, [activeDomain, typeFilter, notify]);

  useEffect(() => { loadItems(); }, [loadItems]);

  // Keep the board current: refetch when the tab regains focus and on a slow
  // poll, so status changes made elsewhere show up without a manual reload.
  useEffect(() => {
    const onVisible = () => { if (!document.hidden) loadItems(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    const interval = setInterval(() => { if (!document.hidden) loadItems(); }, 60000);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
      clearInterval(interval);
    };
  }, [loadItems]);

  const visibleItems = useMemo(
    () => (statusFilter ? items.filter((i) => i._isNew || i.category === statusFilter) : items),
    [items, statusFilter]
  );

  const selected = useMemo(() => items.find((i) => i.id === selectedId) || null, [items, selectedId]);
  const itemAuthorName = useAuthorNames(items.map((i) => i.authorId));

  // JIRA-style: + creates an inline draft, selects it, and the detail panel
  // shows the editable "first row" title form. No popup.
  const startNewRequest = () => {
    if (items.some((i) => i._isNew)) { // one draft at a time
      const existing = items.find((i) => i._isNew);
      setSelectedId(existing.id);
      return;
    }
    const draft = { id: `${DRAFT_PREFIX}${Date.now()}`, _isNew: true, type: "featureRequest", name: "", desc: "", category: "open", timestamp: Date.now() };
    setItems((prev) => [draft, ...prev]);
    setSelectedId(draft.id);
  };

  const cancelDraft = () => {
    setItems((prev) => prev.filter((i) => !i._isNew));
    setSelectedId(null);
  };

  const createFromDraft = async ({ type, title, desc, buttonKey }) => {
    setSaving(true);
    try {
      const nextTicketNumber = Math.max(0, ...items.map((i) => Number(i?.data?.ticketNumber) || 0)) + 1;
      const block = await apiCreateBlock({
        schemaType: SCHEMA_ITEM,
        type,
        name: title.trim(),
        desc: (desc || "").trim(),
        category: "open",
        data: { ticketNumber: nextTicketNumber, ...(buttonKey ? { buttonKey, buttonLabel: buttonLabelForKey(buttonKey) } : {}) },
        ...(activeDomain ? { domain: activeDomain } : {}),
        ...(authorId ? { authorId } : {}),
      });
      if (block) {
        setItems((prev) => [block, ...prev.filter((i) => !i._isNew)]);
        setSelectedId(block.id);
        notify("Request created");
      } else {
        cancelDraft();
        loadItems();
      }
    } catch (err) {
      console.error("Failed to create feedback:", err);
      notify("Could not create request", "error");
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (item, nextStatus) => {
    const prev = item.category || "open";
    if (prev === nextStatus) return;
    try {
      const updated = await apiUpdateBlock({ id: item.id, category: nextStatus, updateNote: `status: ${prev} -> ${nextStatus}` });
      const merged = updated || { ...item, category: nextStatus };
      setItems((list) => list.map((it) => (it.id === item.id ? merged : it)));
      notify("Status updated");
    } catch (err) {
      console.error("Failed to update status:", err);
      notify("Could not update status", "error");
    }
  };

  const saveItemEdits = async (item, { name, desc, data }) => {
    try {
      const updated = await apiUpdateBlock({ id: item.id, name, desc, ...(data ? { data } : {}), updateNote: "edited request" });
      const merged = updated || { ...item, name, desc, ...(data ? { data } : {}) };
      setItems((list) => list.map((it) => (it.id === item.id ? merged : it)));
      notify("Request updated");
    } catch (err) {
      console.error("Failed to save edits:", err);
      notify("Could not save changes", "error");
    }
  };

  // Persist a patch onto the block's freeform data bag (e.g. data.ingestFiles).
  // Returns the merged item so callers can keep working against fresh data.
  const saveItemData = async (item, dataPatch) => {
    const nextData = { ...(item.data || {}), ...dataPatch };
    try {
      const updated = await apiUpdateBlock({
        id: item.id,
        data: nextData,
        updateNote: "data ingest attachment",
      });
      const merged = updated || { ...item, data: nextData };
      setItems((list) => list.map((it) => (it.id === item.id ? merged : it)));
      return merged;
    } catch (err) {
      console.error("Failed to save item data:", err);
      notify("Could not save attachment", "error");
      throw err;
    }
  };

  const softDelete = async (item) => {
    if (item._isNew) { cancelDraft(); return; }
    try {
      await apiUpdateBlock({ id: item.id, category: "deleted", updateNote: `status: ${item.category || "open"} -> deleted` });
      setItems((list) => list.filter((it) => it.id !== item.id));
      if (selectedId === item.id) setSelectedId(null);
      notify("Request deleted");
    } catch (err) {
      console.error("Failed to delete:", err);
      notify("Could not delete request", "error");
    }
  };

  const compactSelectSx = {
    width: 130,
    "& .MuiOutlinedInput-root": { height: 26, fontSize: 12, backgroundColor: "#fff" },
    "& .MuiSelect-select": { textAlign: "left", display: "flex", alignItems: "center" },
  };

  return (
    <Box sx={{ display: "flex", flexDirection: isMobile ? "column" : "row", height: "100%", minHeight: 0, minWidth: 0, overflow: "hidden", gap: isMobile ? 0.75 : 1 }}>
      {/* List panel */}
      <ResizablePanel edge="right" defaultWidth={isMobile ? window.innerWidth : 360} minWidth={isMobile ? window.innerWidth : 280} maxWidth={isMobile ? window.innerWidth : 520} sx={isMobile ? { width: "100% !important", height: "auto", flex: "0 0 45%", minHeight: 0 } : undefined}>
        <AddressbookPanelShell
          titleContent={
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flex: 1, minWidth: 0, flexWrap: "wrap" }}>
              <TextField
                select size="small" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
                SelectProps={{ displayEmpty: true, renderValue: (v) => (v ? typeMeta(v).label : "All Types") }}
                sx={compactSelectSx}
              >
                <MenuItem value="" sx={{ fontSize: 12 }}>All Types</MenuItem>
                {TYPES.map((t) => <MenuItem key={t.value} value={t.value} sx={{ fontSize: 12 }}>{t.label}</MenuItem>)}
              </TextField>
              <TextField
                select size="small" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                SelectProps={{ displayEmpty: true, renderValue: (v) => (v ? statusMeta(v).label : "All Statuses") }}
                sx={compactSelectSx}
              >
                <MenuItem value="" sx={{ fontSize: 12 }}>All Statuses</MenuItem>
                {STATUSES.map((s) => <MenuItem key={s.value} value={s.value} sx={{ fontSize: 12 }}>{s.label}</MenuItem>)}
              </TextField>
            </Box>
          }
          headerActions={
            <IconButton
              size="small" onClick={startNewRequest} title="New request"
              sx={{ backgroundColor: "#16a34a", "&:hover": { backgroundColor: "#15803d" }, width: 24, height: 24, p: 0 }}
            >
              <AddIcon sx={{ fontSize: 16, color: "#FFFFFF" }} />
            </IconButton>
          }
          footerLeft={`${visibleItems.length} request${visibleItems.length === 1 ? "" : "s"}`}
        >
          {loading ? (
            <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}><CircularProgress size={20} /></Box>
          ) : visibleItems.length === 0 ? (
            <Box sx={{ py: 4, textAlign: "center" }}>
              <Typography sx={{ color: "text.secondary", fontSize: 13 }}>No requests yet.</Typography>
              <Typography sx={{ color: "text.secondary", fontSize: 11 }}>Use the + button to add one.</Typography>
            </Box>
          ) : (
            <Stack spacing={1}>
              {visibleItems.map((item) => (
                <FeedbackCard
                  key={item.id}
                  item={item}
                  selected={item.id === selectedId}
                  onSelect={(it) => setSelectedId(it.id)}
                  onDelete={softDelete}
                  onStatusChange={changeStatus}
                  authorName={itemAuthorName(item.authorId)}
                />
              ))}
            </Stack>
          )}
        </AddressbookPanelShell>
      </ResizablePanel>

      {/* Detail panel */}
      <ResizablePanel edge="right" defaultWidth={isMobile ? window.innerWidth : 700} minWidth={isMobile ? window.innerWidth : 300} maxWidth={isMobile ? window.innerWidth : 700} fillRemaining={!isMobile} sx={isMobile ? { width: "100% !important", height: "auto", flex: "1 1 auto", minHeight: 0 } : undefined}>
        {selected && !selected._isNew ? (
          <FeedbackDetailPanel
            key={selected.id}
            item={selected}
            authorId={authorId}
            domain={activeDomain}
            onStatusChange={(next) => changeStatus(selected, next)}
            onSave={saveItemEdits}
            onSaveData={saveItemData}
            onDelete={softDelete}
          />
        ) : (
          <AddressbookPanelShell title={selected?._isNew ? "New Request" : ""}>
            {selected?._isNew ? (
              <NewRequestForm onCreate={createFromDraft} onCancel={cancelDraft} saving={saving} />
            ) : (
              <Box sx={{ py: 6, textAlign: "center" }}>
                <Typography sx={{ color: "text.secondary", fontSize: 14 }}>Select a request to view details</Typography>
              </Box>
            )}
          </AddressbookPanelShell>
        )}
      </ResizablePanel>

      <Snackbar
        open={toast.open}
        autoHideDuration={3500}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={toast.severity} onClose={() => setToast((t) => ({ ...t, open: false }))}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PageFeedback;
